import React from 'react';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {getLegacyRequestObjectName, RequestSchema} from 'requests/RequestConstants';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import SelectDropdown, {closeSelectDropdown} from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface ConnectedProps {
  requestSchemas: Array<RequestSchema>;
}

class RequestSchemaSelectorConnector extends DataConnector<
  RequestSchemaSelectorProps | RequestSchemaSelectorIdInputProps,
  ConnectedProps
> {
  override stores = [AssayStore];

  enhanceProps(): ConnectedProps {
    const requestSchemas = AssayStore.getRequestSchemas();
    if (requestSchemas == null) {
      return this.fallBackToLoading();
    }

    return {
      requestSchemas: requestSchemas.filter((schema: RequestSchema) =>
        this.props.isRequestSchemaValid(schema)
      ),
    };
  }
}

const DEFAULT_SHOULD_SHOW_NO_SCHEMA_OPTION = false;
interface BaseRequestSchemaSelectorProps {
  selectedRequestSchema: RequestSchema | null;
  isRequestSchemaValid: (requestSchema: RequestSchema) => boolean;
  // requestSchema may be null if `shouldShowNoSchemaOption` is true.  Otherwise, it should not be null.
  onRequestSchemaChange: (requestSchema: RequestSchema | null) => void;
  requestSchemas: Array<RequestSchema>;
  shouldShowNoSchemaOption?: boolean;
}
class BaseRequestSchemaSelector extends React.Component<BaseRequestSchemaSelectorProps> {
  dropdownRef = React.createRef<SelectDropdown>();

  override componentDidMount(): void {
    AssaySchemaActions.fetchAllAssaySchemas();
  }

  _handleRequestSchemaChange = (requestSchemaId: string | null): void => {
    if (requestSchemaId == null) {
      this.props.onRequestSchemaChange(null);
      return;
    }

    const requestSchema = this.props.requestSchemas.find((schema) => schema.id === requestSchemaId);
    if (requestSchema == null) {
      assert(false, 'Request schema not found');
      return;
    }
    this.props.onRequestSchemaChange(requestSchema);
    if (this.dropdownRef.current != null) {
      closeSelectDropdown(this.dropdownRef.current);
    }
  };

  override render(): JSX.Element {
    if (this.props.requestSchemas.length === 0) {
      return (
        <div>{`No ${getLegacyRequestObjectName({lowerCase: true})} schemas found with matching sample type.`}</div>
      );
    }
    const requestSchema = this.props.selectedRequestSchema;
    const shouldShowNoSchemaOption =
      this.props.shouldShowNoSchemaOption ?? DEFAULT_SHOULD_SHOW_NO_SCHEMA_OPTION;
    const optionDatas = [];

    if (shouldShowNoSchemaOption) {
      optionDatas.push({name: 'No schema', value: null});
      optionDatas.push({name: 'Schemas', isHeader: true});
    }
    optionDatas.push(
      ...this.props.requestSchemas

        .filter((schema) => !isArchived(schema))
        .map((schema) => ({
          value: schema.id,
          name: schema.name,
        }))
    );

    return (
      <div>
        <SelectDropdown
          disabled={false}
          displayName={requestSchema == null ? 'Select...' : requestSchema.name}
          closeOnInsideClick={true}
          closeOnOutsideClick={true}
          menu={
            <SearchableMenu
              onSelect={this._handleRequestSchemaChange}
              optionDatas={optionDatas}
              selectedValue={requestSchema == null ? null : requestSchema.id}
              width={Menu.WIDTHS.FULL}
            />
          }
          ref={this.dropdownRef}
        />
      </div>
    );
  }
}

interface RequestSchemaSelectorProps extends Omit<BaseRequestSchemaSelectorProps, 'requestSchemas'> {}
export function RequestSchemaSelector(props: RequestSchemaSelectorProps & ConnectedProps): JSX.Element {
  return <BaseRequestSchemaSelector {...props} />;
}

interface RequestSchemaSelectorIdInputProps
  extends Omit<RequestSchemaSelectorProps, 'selectedRequestSchema'> {
  selectedRequestSchemaId: string | null;
}

export function RequestSchemaSelectorId(
  props: RequestSchemaSelectorIdInputProps & ConnectedProps
): JSX.Element {
  const selectedRequestSchema =
    props.requestSchemas.find((requestSchema) => requestSchema.id === props.selectedRequestSchemaId) ?? null;

  return <BaseRequestSchemaSelector {...props} selectedRequestSchema={selectedRequestSchema} />;
}

export default RequestSchemaSelectorConnector.connect(RequestSchemaSelector);
export const RequestSchemaSelectorIdInput = RequestSchemaSelectorConnector.connect(RequestSchemaSelectorId);
